import React from 'react';
import { CreditCard, X, Landmark } from 'lucide-react';
import { formatMoney } from '../../utils/format';
import { BANKS, MIN_COMPANY_AGE_FOR_LOAN } from '../../data/bankData';
import { LoanProposalCard } from './LoanProposalCard';

export function CreditTab({
  pendingProposals, loanAmount, setLoanAmount, loanDuration, setLoanDuration,
  reputation, vehicles, monthlyRevenues, totalDebt, totalDays,
  requestLoanProposals, acceptLoanProposal, dismissProposals,
}) {
  // 13/06/2026 — Jeune entreprise : les banques exigent 3 mois d'historique.
  const tooYoung = Number.isFinite(totalDays) && totalDays < MIN_COMPANY_AGE_FOR_LOAN;
  return (
    <div className="space-y-4">
      {tooYoung && (
        <div
          className="bg-warn/10 border border-warn/40 rounded-lg px-4 py-3 flex items-start gap-3"
          data-testid="loan-too-young-banner"
        >
          <Landmark className="w-5 h-5 text-warn flex-shrink-0 mt-0.5" />
          <div className="text-xs text-slate-300 leading-relaxed">
            <p className="font-bold text-warn mb-0.5">Les banquiers sont unanimes : revenez avec un bilan.</p>
            <p>
              « Votre société n&apos;a que <strong>{totalDays} jours</strong> d&apos;activité. Aucun établissement
              n&apos;accorde de crédit professionnel sans au moins <strong>3 mois d&apos;historique
              d&apos;exploitation</strong> (jour {MIN_COMPANY_AGE_FOR_LOAN}). D&apos;ici là, optimisez votre trésorerie :
              contrats réguliers, leasing constructeur, et maîtrise des charges. »
            </p>
          </div>
        </div>
      )}
      {!pendingProposals ? (
        <div className="bg-ink-800 rounded-lg border border-ink-600 p-4">
          <h3 className="text-sm font-semibold text-slate-200 mb-4 flex items-center gap-2">
            <CreditCard className="w-4 h-4" /> Demande de financement
          </h3>

          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-xs text-slate-500 mb-1">
                <span>Montant souhaité</span>
                <span className="font-bold text-slate-300">{formatMoney(loanAmount)}</span>
              </div>
              <input
                type="range" min={5000} max={300000} step={5000}
                value={loanAmount} onChange={e => setLoanAmount(Number(e.target.value))}
                className="w-full accent-[#1F3864]"
                data-testid="loan-amount-slider"
              />
              <div className="flex justify-between text-[10px] text-slate-500"><span>5 000 €</span><span>300 000 €</span></div>
            </div>

            <div>
              <div className="flex justify-between text-xs text-slate-500 mb-1">
                <span>Durée</span>
                <span className="font-bold text-slate-300">{loanDuration} mois</span>
              </div>
              <input
                type="range" min={12} max={60} step={6}
                value={loanDuration} onChange={e => setLoanDuration(Number(e.target.value))}
                className="w-full accent-[#1F3864]"
                data-testid="loan-duration-slider"
              />
              <div className="flex justify-between text-[10px] text-slate-500"><span>12 mois</span><span>60 mois</span></div>
            </div>

            <div className="bg-accent/10 rounded-lg p-3 text-xs text-accent">
              <p className="font-medium mb-1">Votre profil emprunteur :</p>
              <div className="grid grid-cols-2 gap-1 text-[10px]">
                <span>Réputation : <strong>{Math.round(reputation)}/100</strong></span>
                <span>Flotte : <strong>{vehicles.length} véhicule(s)</strong></span>
                <span>CA mensuel : <strong>{formatMoney(monthlyRevenues)}</strong></span>
                <span>Dette actuelle : <strong>{formatMoney(totalDebt)}</strong></span>
              </div>
            </div>

            <button
              onClick={() => requestLoanProposals(loanAmount, loanDuration)}
              className="w-full bg-accent text-white py-3 rounded-lg text-sm font-bold hover:bg-accent-hover transition-colors"
              data-testid="request-proposals-btn"
            >
              Demander les propositions des 3 banques
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-200">
              Propositions pour {formatMoney(pendingProposals.amount)} sur {pendingProposals.durationMonths} mois
            </h3>
            <button onClick={dismissProposals} className="text-xs text-slate-500 hover:text-slate-400 flex items-center gap-1" data-testid="dismiss-proposals-btn">
              <X className="w-3.5 h-3.5" /> Annuler
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {pendingProposals.proposals.map(p => (
              <LoanProposalCard
                key={p.bankId}
                proposal={p}
                bank={BANKS[p.bankId]}
                durationMonths={pendingProposals.durationMonths}
                onAccept={acceptLoanProposal}
              />
            ))}
          </div>

          {/* Multi-bank tip — encourages the player to spread debt across
              relationships rather than maxing out a single lender. */}
          <div
            className="text-[11px] text-slate-400 bg-accent/10 border border-accent/30 rounded-lg px-3 py-2 leading-relaxed"
            data-testid="multi-bank-tip"
          >
            💡 <strong className="text-accent">Astuce :</strong> chaque banque applique son plafond indépendamment.
            Diversifier votre dette sur 2-3 banques augmente votre capacité totale d&apos;emprunt
            et améliore votre relation auprès de chacune.
          </div>
        </div>
      )}
    </div>
  );
}
